export const features = [
  {
    title: 'Overview',
    items: [
      {
        title: 'Teams',
        description:
          'Teams are groups of users who own one or more services, typically separated for access control.',
        values: {
          free: 1,
          pro: 3,
          enterprise: 5,
        },
      },
      {
        title: 'Installed Agents',
        description:
          'Any Agent that is installed and authenticated with Gremlin (on a host, containerized environment, or within an application)',
        values: {
          free: 1,
          pro: 'Unlimited',
          enterprise: 'Unlimited',
        },
      },
      {
        title: 'Attacks per Target',
        description:
          'Attacks are different ways to test your system to uncover vulnerabilities. These are broken down into State, Resource, Network, and Application Level Attacks.',
        values: {
          free: 'Unlimited',
          pro: 'Unlimited',
          enterprise: 'Unlimited',
        },
      },
      {
        title: 'Attacked Targets',
        description:
          'Attacked Targets are the total number of unique Targets that have run at least one attack over a month for Free, a week for Professional, and a day for Enterprise.',
        values: {
          free: '2 per month',
          pro: '30 per week',
          enterprise: '100 per week',
        },
      },
      {
        title: 'Large scale experiments',
        description:
          'Two days of unlimited Attacked Targets to perform large scale tests.',
        values: {
          free: false,
          pro: false,
          enterprise: '2 per year',
        },
      },
      {
        title: 'Support',
        description:
          'Community Slack and documentation or Professional and Enterprise support by our Chaos Engineering experts.',
        values: {
          free: ['Gremlin Community', '/slack/'],
          pro: ['Standard, CSM', 'mailto:support@gremlin.com'],
          enterprise: ['Standard, CSM, TAM', 'mailto:support@gremlin.com'],
        },
      },
    ],
  },
  {
    title: 'Agents / Clients',
    items: [
      {
        title: 'Linux',
        description:
          'Safely perform experiments on Linux hosts including Ubuntu, Debian, RHEL, CentOS, Alpine, and more.',
        values: {
          free: true,
          pro: true,
          enterprise: true,
        },
      },
      {
        title: 'Windows',
        description:
          'Perform chaos experiments in your Windows environment to improve their reliability.',
        values: {
          free: true,
          pro: true,
          enterprise: true,
        },
      },
      {
        title: 'Containers',
        description:
          'Leverage Container discovery to target containers with experiments in complex and ephemeral environments.',
        values: {
          free: true,
          pro: true,
          enterprise: true,
        },
      },
      {
        title: 'Kubernetes',
        description:
          'Easily discover, visualize, and target ephemeral Kubernetes objects.',
        values: {
          free: true,
          pro: true,
          enterprise: true,
        },
      },
    ],
  },
  {
    title: 'Attack types',
    items: [
      {
        title: 'State',
        description:
          'Shut down hosts, time travel, and kill processes so that you can observe how well your service handles infrastructure failure.',
        values: {
          free: 3,
          pro: 3,
          enterprise: 3,
        },
      },
      {
        title: 'Resource',
        description:
          'Reveal how your service degrades or auto-scales when starved of CPU, memory, IO, or disk.',
        values: {
          free: 4,
          pro: 4,
          enterprise: 4,
        },
      },
      {
        title: 'Network',
        description:
          'Test how your service behaves when you are unable to reach one or more of your dependencies, internal or external, in a limited and controlled manner.',
        values: {
          free: 4,
          pro: 4,
          enterprise: 4,
        },
      },
    ],
  },
  {
    title: 'Fine-grained control and safety',
    items: [
      {
        title: 'Halt, revert, & auto kill switch',
        description:
          'Halting attacks if they exceed predefined thresholds or SLOs through the UI, API, or monitoring integrations allows you to run experiments with confidence.',
        values: {
          free: true,
          pro: true,
          enterprise: true,
        },
      },
      {
        title: 'Magnitude and Blast Radius',
        description:
          'Limit the Blast Radius and magnitude of your experiments to minimize the impact on your Teams and precisely diagnose system behavior.',
        values: {
          free: true,
          pro: true,
          enterprise: true,
        },
      },
      {
        title: 'Scheduled Attacks',
        description:
          'Schedule Attacks to execute on certain days and within a specified time window from the Web App or programmatically.',
        values: {
          free: true,
          pro: true,
          enterprise: true,
        },
      },
      {
        title: 'Scenarios',
        description:
          'A series of Gremlin Attacks that replicate real world incidents.',
        values: {
          free: true,
          pro: true,
          enterprise: true,
        },
      },
      {
        title: 'Services Discovery',
        description:
          'Auto-detect your distributed services, perform chaos experiments on them, and track your reliability practice.',
        values: {
          free: true,
          pro: true,
          enterprise: true,
        },
      },
    ],
  },
  {
    title: 'Enterprise administration',
    items: [
      {
        title: 'UI',
        description:
          'Gremlin’s intuitive interface with built-in guidance allows you to easily and precisely design chaos experiments.',
        values: {
          free: true,
          pro: true,
          enterprise: true,
        },
      },
      {
        title: 'CLI',
        description:
          'Gremlin’s CLI allows the user to perform commands directly from the host.',
        values: {
          free: true,
          pro: true,
          enterprise: true,
        },
      },
      {
        title: 'API',
        description:
          'Our elegant API provides the ability to automate experiments and add Gremlin into your build pipelines.',
        values: {
          free: true,
          pro: true,
          enterprise: true,
        },
      },
      {
        title: 'Google SSO',
        description:
          'Simply log in to Gremlin’s application with your existing Google credentials.',
        values: {
          free: true,
          pro: true,
          enterprise: true,
        },
      },
      {
        title: 'MFA',
        description:
          'Add an extra layer of protection for user accounts with Time-based Tokens to ensure passwords are not the only line of defense.',
        values: {
          free: true,
          pro: true,
          enterprise: true,
        },
      },
      {
        title: 'SAML',
        description:
          'Use your own identity provider (IDP) to simplify logins into the application.',
        values: {
          free: false,
          pro: true,
          enterprise: true,
        },
      },
      {
        title: 'RBAC',
        description:
          'Control your user access with specific role-based permissions for your users.',
        values: {
          free: false,
          pro: true,
          enterprise: true,
        },
      },
      {
        title: 'Audit Trail',
        description:
          'All-access to the system is logged and made available to administrators to facilitate integration with customer security monitoring systems.',
        values: {
          free: false,
          pro: true,
          enterprise: true,
        },
      },
    ],
  },
]

export const tiers = [
  {
    title: 'Free',
    sub: 'For getting started',
    price: {
      amount: '$0',
      sub: 'Forever',
    },
    description:
      'Familiarize yourself with Chaos Engineering by safely experimenting with a limited blast radius.',
    features: [
      '1 Team',
      '1 Installed Agent',
      'Unlimited Attacks per Target',
      '2 Attacked Targets per month',
      ['Community Support', '/slack/'],
    ],
  },
  {
    title: 'Pro',
    sub: 'For small organizations',
    price: {
      amount: 'Contact us',
      sub: 'to schedule a call',
    },
    description:
      'For small organizations with a limited number of Teams and applications. Adds security controls and compliance.',
    features: [
      '3 Teams',
      'Unlimited Installed Agents',
      'Unlimited Attacks per Target',
      '30 Attacked Targets per week',
      ['Standard Support, CSM', 'mailto:support@gremlin.com'],
    ],
  },
  {
    title: 'Enterprise',
    sub: 'For mid-large organizations',
    price: {
      amount: 'Contact us',
      sub: 'to schedule a call',
    },
    description:
      'For multiple teams running and automating experiments. Includes security controls and production support.',
    features: [
      '5 Teams',
      'Unlimited Installed Agents',
      'Unlimited Attacks per Target',
      '100 Attacked Targets per week',
      '2 Large scale experiments',
      ['Standard support, CSM, TAM', 'mailto:support@gremlin.com'],
    ],
  },
]

export const addons = [
  {
    title: 'Pro',
    items: ['1 additional Team', '10 additional Attacked Targets per week'],
  },
  {
    title: 'Enterprise',
    items: ['1 additional Team', '20 additional Attacked Targets per week'],
  },
]
